import { z } from "zod";

export const roleValues = ["owner", "admin", "manager", "staff", "accountant", "viewer"] as const;
export const bookingStatusValues = [
  "draft",
  "pending",
  "confirmed",
  "rescheduled",
  "completed",
  "cancelled",
  "no-show",
  "refunded"
] as const;
export const weekdayValues = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
] as const;
export const giftCardStatusValues = ["active", "inactive"] as const;
export const giftCardOrderStatusValues = ["pending", "fulfilled"] as const;

export const roleSchema = z.enum(roleValues);
export const bookingStatusSchema = z.enum(bookingStatusValues);
export const weekdaySchema = z.enum(weekdayValues);

export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const timeSchema = z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/);
export const isoDateTimeSchema = z.string().datetime({ offset: true });
export const moneyAmountSchema = z.number().int().min(0);

export function normalizeAdminDetailsRecord(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const normalized: Record<string, string> = {};
  for (const [key, rawValue] of Object.entries(value)) {
    if (rawValue == null) {
      continue;
    }

    if (typeof rawValue === "string") {
      normalized[key] = rawValue;
      continue;
    }

    if (typeof rawValue === "number" || typeof rawValue === "boolean") {
      normalized[key] = String(rawValue);
    }
  }

  return normalized;
}

export const adminDetailsInputSchema = z
  .unknown()
  .optional()
  .transform((value): Record<string, string> => normalizeAdminDetailsRecord(value));

export const optionalAdminDetailsInputSchema = z
  .unknown()
  .transform((value): Record<string, string> => normalizeAdminDetailsRecord(value))
  .optional();

function coercePositiveIntegerArray(value: unknown, fallback: number[]) {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : [];
  const normalized = rawValues
    .map((entry) => Number(String(entry ?? "").trim()))
    .filter((entry) => Number.isFinite(entry) && entry > 0)
    .map((entry) => Math.round(entry));

  return normalized.length ? normalized : fallback;
}

function coerceTimeArray(value: unknown) {
  if (value == null) {
    return undefined;
  }

  return (Array.isArray(value) ? value : typeof value === "string" ? value.split(/[,\n|]/) : [])
    .map((entry) => String(entry ?? "").trim())
    .filter(Boolean);
}

function coerceStringArray(value: unknown) {
  if (value == null) {
    return undefined;
  }

  return (Array.isArray(value) ? value : typeof value === "string" ? value.split(/[,\n|]/) : [])
    .map((entry) => String(entry ?? "").trim())
    .filter(Boolean);
}

export const defaultCustomerSuppliesEquipmentItems = [
  "Vacuum cleaner",
  "Mop and bucket or reusable floor mop system",
  "All-purpose cleaner",
  "Bathroom cleaner",
  "Glass cleaner",
  "Floor cleaner suitable for your floors",
  "Sponges, scrub pads, and microfiber towels",
  "Trash bags and paper towels",
  "Toilet brush",
  "Step stool, if high dusting is requested"
];

export const businessHoursItemSchema = z.object({
  day: weekdaySchema,
  start: timeSchema,
  end: timeSchema,
  enabled: z.boolean().default(true)
});

export const addressSchema = z.object({
  label: z.string().default("Service address"),
  line1: z.string().default(""),
  line2: z.string().default(""),
  unitNumber: z.string().default(""),
  city: z.string().default(""),
  state: z.string().default(""),
  postalCode: z.string().default(""),
  country: z.string().default("US"),
  instructions: z.string().default(""),
  isPrimary: z.boolean().default(false)
});

export const sessionUserSchema = z.object({
  userId: z.string().min(1),
  accountId: z.string().min(1),
  email: z.string().email(),
  name: z.string().min(1),
  staffId: z.string().min(1).nullable().optional(),
  roles: z.array(roleSchema).default([])
});

export const loginInputSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  turnstileToken: z.string().optional()
});

export const loginMfaChallengeResponseSchema = z.object({
  mfaRequired: z.literal(true),
  challengeToken: z.string().min(1),
  phoneHint: z.string().default(""),
  expiresAt: z.string().min(1)
});

export const loginMfaVerifyInputSchema = z.object({
  challengeToken: z.string().min(1),
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code.")
});

export const loginMfaResendInputSchema = z.object({
  challengeToken: z.string().min(1)
});

export const mfaPhoneInputSchema = z.object({
  phone: z.string().min(7)
});

export const customerPortalSessionSchema = z.object({
  customerId: z.string().min(1),
  accountId: z.string().min(1),
  email: z.string().email(),
  name: z.string().min(1)
});

export const customerMagicLinkRequestSchema = z.object({
  email: z.string().email(),
  turnstileToken: z.string().optional()
});

export const customerMagicLinkConsumeSchema = z.object({
  token: z.string().min(1)
});

export const customerEmailPreferencesSchema = z
  .object({
    smsOptInStatus: z.boolean().default(false),
    smsReminderOptInStatus: z.boolean().default(false),
    emailOptInStatus: z.boolean().default(true),
    bookingConfirmation: z.boolean().default(true),
    reminders: z.boolean().default(true),
    receipts: z.boolean().default(true),
    marketing: z.boolean().default(true)
  })
  .default({
    smsOptInStatus: false,
    smsReminderOptInStatus: false,
    emailOptInStatus: true,
    bookingConfirmation: true,
    reminders: true,
    receipts: true,
    marketing: true
  });

export const customerPortalProfileUpdateSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().min(7),
  secondaryPhone: z.string().default(""),
  emailPreferences: customerEmailPreferencesSchema,
  address: addressSchema
    .extend({
      isPrimary: z.boolean().default(true)
    })
    .default({
      label: "Service address",
      line1: "",
      line2: "",
      unitNumber: "",
      city: "",
      state: "",
      postalCode: "",
      country: "US",
      instructions: "",
      isPrimary: true
    })
});

export const customerInputSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  companyName: z.string().default(""),
  profilePhotoUrl: z.string().default(""),
  customerType: z.string().default("Residential"),
  accountStatus: z.string().default("Active"),
  email: z.string().email().optional().or(z.literal("")),
  phone: z.string().min(7),
  secondaryPhone: z.string().default(""),
  pets: z.string().default(""),
  sensitivities: z.string().default(""),
  documents: z.array(z.string()).default([]),
  notes: z.string().optional(),
  serviceFrequency: z.string().default(""),
  blockedUntil: isoDateSchema.optional().or(z.literal("")),
  blockedReason: z.string().default(""),
  preferredStaffIds: z.array(z.string()).default([]),
  blockedStaffIds: z.array(z.string()).default([]),
  preferredContactMethod: z.string().default("Phone"),
  englishSpeakingStaff: z.boolean().default(false),
  leadSourceId: z.string().optional().or(z.literal("")),
  stripeCustomerId: z.string().default(""),
  paymentMethods: z.array(z.string()).default([]),
  propertyPhotos: z.array(z.string()).default([]),
  refundsDisputes: z.array(z.string()).default([]),
  problemAreas: z.array(z.string()).default([]),
  damageReports: z.array(z.string()).default([]),
  specialIncidents: z.array(z.string()).default([]),
  clientRating: z.number().min(0).max(5).optional(),
  customerSatisfactionScore: z.number().min(0).max(100).optional(),
  rewards: z
    .object({
      points: z.number().int().min(0).default(0),
      tier: z.string().default(""),
      notes: z.string().default("")
    })
    .default({
      points: 0,
      tier: "",
      notes: ""
    }),
  emailPreferences: customerEmailPreferencesSchema,
  taxExempt: z.boolean().default(false),
  specialTaxRate: z.number().min(0).max(1).optional(),
  address: addressSchema.optional(),
  addresses: z.array(addressSchema).default([]),
  tagIds: z.array(z.string()).default([])
});

export const availabilityQuerySchema = z.object({
  locationId: z.string().min(1),
  serviceId: z.string().min(1),
  bookingSpotId: z.string().optional(),
  calendarProfileId: z.string().optional(),
  optionIds: z.array(z.string()).default([]),
  adminDetails: adminDetailsInputSchema,
  date: isoDateSchema,
  partySize: z.number().int().min(1).default(1)
});

export const quoteRequestSchema = z.object({
  locationId: z.string().min(1),
  serviceId: z.string().min(1),
  bookingSpotId: z.string().optional(),
  optionIds: z.array(z.string()).default([]),
  frequencyRuleId: z.string().optional(),
  calendarProfileId: z.string().optional(),
  startsAt: isoDateTimeSchema.optional(),
  serviceDate: isoDateSchema.optional(),
  promoCode: z.string().optional(),
  giftCardCode: z.string().optional(),
  adminDetails: adminDetailsInputSchema,
  taxRate: z.number().min(0).max(1).default(0),
  depositRate: z.number().min(0).max(1).default(0.2)
});

export const bookingCreateInputSchema = z.object({
  customer: customerInputSchema,
  locationId: z.string().min(1),
  serviceId: z.string().min(1),
  bookingSpotId: z.string().optional(),
  optionIds: z.array(z.string()).default([]),
  frequencyRuleId: z.string().optional(),
  calendarProfileId: z.string().optional(),
  startsAt: isoDateTimeSchema,
  notes: z.string().default(""),
  internalNotes: z.string().default(""),
  promoCode: z.string().optional(),
  giftCardCode: z.string().optional(),
  source: z.string().default("public"),
  depositChoice: z.enum(["none", "deposit", "full"]).default("deposit"),
  paymentIntentId: z.string().optional(),
  setupIntentId: z.string().optional(),
  waitlistRequestId: z.string().optional(),
  waitlistRequestToken: z.string().optional(),
  turnstileToken: z.string().optional(),
  legalConsentAccepted: z.boolean().default(false),
  adminDetails: adminDetailsInputSchema
});

export const publicBookingCreateInputSchema = bookingCreateInputSchema.extend({
  customer: customerInputSchema.extend({
    email: z.string().email()
  })
});

const giftCardPublicEmailSchema = z.union([z.string().email(), z.literal("")]).default("");
const giftCardTimedDeliverySchema = z.union([z.string().datetime({ offset: true }), z.literal("")]).default("");
const giftCardCustomBackgroundUrlSchema = z.union([z.string().url(), z.literal("")]).default("");

export const giftCardPurchaseSessionInputSchema = z.object({
  amount: moneyAmountSchema.min(1000),
  purchaserName: z.string().min(1),
  purchaserEmail: z.string().email(),
  recipientName: z.string().min(1),
  recipientEmail: giftCardPublicEmailSchema,
  message: z.string().max(500).default(""),
  giftCardThemeId: z.string().default(""),
  customBackgroundUrl: giftCardCustomBackgroundUrlSchema,
  scheduledFor: giftCardTimedDeliverySchema
});

const adminGiftCardEmailSchema = giftCardPublicEmailSchema;

const giftCardAdminIssueBaseSchema = z.object({
  amount: moneyAmountSchema.min(1000),
  purchaserName: z.string().min(1),
  purchaserEmail: adminGiftCardEmailSchema,
  recipientName: z.string().min(1),
  recipientEmail: adminGiftCardEmailSchema,
  message: z.string().max(500).default(""),
  giftCardThemeId: z.string().default(""),
  internalNote: z.string().default("")
});

export const giftCardAdminPurchaseSessionInputSchema = giftCardAdminIssueBaseSchema;

export const giftCardAdminFreeIssueInputSchema = giftCardAdminIssueBaseSchema;

export const giftCardLegacyImportSchema = z.object({
  cardsCsv: z.string().min(1),
  redemptionsCsv: z.string().default(""),
  changesCsv: z.string().default(""),
  sendEmails: z.boolean().default(false)
});

export const giftCardManualIssueInputSchema = giftCardPurchaseSessionInputSchema.extend({
  note: z.string().default("")
});

export const giftCardAdjustmentInputSchema = z.object({
  amountDelta: z.number().int().refine((value) => value !== 0, "Adjustment amount cannot be zero"),
  note: z.string().min(1)
});

export const giftCardStatusUpdateInputSchema = z.object({
  status: z.enum(giftCardStatusValues)
});

const giftCardThemeColorSchema = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Use a hex color like #c65f3f");
const giftCardThemeBackgroundUrlSchema = z.union([z.string().url(), z.literal("")]);

export const giftCardThemeSnapshotSchema = z.object({
  id: z.string().default(""),
  name: z.string().default("Just Because"),
  slug: z.string().default("just-because"),
  occasionType: z.string().default("general"),
  backgroundImageUrl: giftCardThemeBackgroundUrlSchema.default(""),
  accentColor: giftCardThemeColorSchema.default("#c65f3f"),
  textColor: giftCardThemeColorSchema.default("#18261f"),
  headline: z.string().default("A thoughtful gift for you"),
  subheadline: z.string().default("Use this gift card whenever you're ready.")
});

export const giftCardThemeInputSchema = z.object({
  name: z.string().min(1),
  slug: z.string().default(""),
  occasionType: z.string().default("general"),
  backgroundImageUrl: giftCardThemeBackgroundUrlSchema.default(""),
  accentColor: giftCardThemeColorSchema.default("#c65f3f"),
  textColor: giftCardThemeColorSchema.default("#18261f"),
  headline: z.string().default(""),
  subheadline: z.string().default(""),
  displayOrder: z.number().int().min(0).default(0),
  active: z.boolean().default(true),
  isDefault: z.boolean().default(false),
  config: z.record(z.string(), z.unknown()).default({})
});

export const giftCardThemeStatusUpdateInputSchema = z.object({
  active: z.boolean(),
  isDefault: z.boolean().optional()
});

export const giftCardThemeSchema = giftCardThemeInputSchema.extend({
  id: z.string().min(1),
  slug: z.string().min(1),
  createdAt: z.string().default(""),
  updatedAt: z.string().default("")
});

export const giftCardStorefrontSchedulingPolicySchema = z.enum(["issue_now_send_later", "issue_on_send"]);
export const giftCardBuyerRevealPolicySchema = z.enum(["follow_schedule", "always_reveal", "hide_until_sent"]);

export const giftCardStorefrontSettingsSchema = z
  .object({
    enabled: z.boolean().default(true),
    heroEyebrow: z.string().default("Gift Cards"),
    heroHeadline: z.string().default("Send a stored-value gift card in minutes."),
    heroSubheadline: z
      .string()
      .default("Choose an amount, add a note, and we'll deliver the code by email right after payment clears."),
    heroImageUrl: giftCardThemeBackgroundUrlSchema.default(""),
    presetAmounts: z.array(moneyAmountSchema.min(1000)).default([5000, 10000, 15000, 20000, 25000, 30000, 50000]),
    minAmount: moneyAmountSchema.min(1000).default(1000),
    maxAmount: moneyAmountSchema.min(1000).default(1000000),
    defaultThemeId: z.string().default(""),
    legalDisclaimer: z.string().default(""),
    printFooterText: z.string().default(""),
    schedulingPolicy: giftCardStorefrontSchedulingPolicySchema.default("issue_now_send_later"),
    buyerRevealPolicy: giftCardBuyerRevealPolicySchema.default("follow_schedule"),
    allowPrintPage: z.boolean().default(true),
    allowPdfDownload: z.boolean().default(true),
    primaryCtaLabel: z.string().default("Continue to payment"),
    secondaryCtaLabel: z.string().default("Book a service")
  })
  .superRefine((value, ctx) => {
    if (value.maxAmount < value.minAmount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["maxAmount"],
        message: "Max amount must be greater than or equal to min amount."
      });
    }

    value.presetAmounts.forEach((amount, index) => {
      if (amount < value.minAmount || amount > value.maxAmount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["presetAmounts", index],
          message: "Preset amount must be between the min and max amount."
        });
      }
    });
  });

export const waitlistPreferredWindowValues = ["any", "morning", "midday", "afternoon"] as const;
export const waitlistStatusValues = ["active", "fulfilled", "expired", "closed"] as const;

export const waitlistPreferredWindowSchema = z.enum(waitlistPreferredWindowValues);
export const waitlistStatusSchema = z.enum(waitlistStatusValues);

export const publicWaitlistCreateInputSchema = z
  .object({
    customer: customerInputSchema.extend({
      email: z.string().email(),
      phone: z.string().default("")
    }),
    locationId: z.string().min(1),
    serviceId: z.string().min(1),
    bookingSpotId: z.string().optional(),
    optionIds: z.array(z.string()).default([]),
    frequencyRuleId: z.string().optional(),
    calendarProfileId: z.string().optional(),
    requestedDate: isoDateSchema,
    preferredWindow: waitlistPreferredWindowSchema.default("any"),
    notifyBySms: z.boolean().default(false),
    source: z.string().default("public_waitlist"),
    turnstileToken: z.string().optional(),
    adminDetails: adminDetailsInputSchema
  })
  .superRefine((value, ctx) => {
    if (value.notifyBySms && String(value.customer.phone ?? "").trim().length < 7) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["customer", "phone"],
        message: "Phone is required when text alerts are enabled."
      });
    }
  });

export const bookingUpdateInputSchema = z.object({
  status: bookingStatusSchema.optional(),
  startsAt: isoDateTimeSchema.optional(),
  notes: z.string().optional(),
  internalNotes: z.string().optional(),
  staffIds: z.array(z.string()).optional(),
  adminDetails: optionalAdminDetailsInputSchema
});

export const paymentIntentRequestSchema = z.object({
  bookingId: z.string().min(1),
  amount: moneyAmountSchema,
  paymentType: z.enum(["deposit", "full", "balance"]).default("deposit")
});

export const refundRequestSchema = z.object({
  amount: moneyAmountSchema,
  reason: z.string().default("requested_by_customer")
});

export const paymentChargeQueueQuerySchema = z.object({
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  status: z
    .enum(["all", "unpaid", "partially_paid", "paid", "on_hold", "refunded"])
    .default("all"),
  method: z
    .enum(["all", "card", "manual", "cash", "check", "paypal", "no_card"])
    .default("all"),
  locationId: z.string().optional().or(z.literal("")),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(10).max(100).default(50)
});

export const paymentChargeBatchInputSchema = z.object({
  actionMode: z.enum(["charge_saved_card", "mark_paid"]),
  bookingIds: z.array(z.string().min(1)).min(1).max(100),
  amountOverrides: z.record(z.string(), moneyAmountSchema).default({})
});

export const paymentHoldInputSchema = z.object({
  reason: z.string().max(500).default("")
});

export const dashboardSummarySchema = z.object({
  bookingsToday: z.number().int(),
  pendingBookings: z.number().int(),
  confirmedBookings: z.number().int(),
  cancelledBookings: z.number().int(),
  revenueToday: moneyAmountSchema,
  revenueWeek: moneyAmountSchema,
  revenueMonth: moneyAmountSchema,
  pendingPayments: moneyAmountSchema,
  topServices: z.array(
    z.object({
      name: z.string(),
      totalRevenue: moneyAmountSchema,
      totalBookings: z.number().int()
    })
  ),
  notifications: z.array(
    z.object({
      type: z.string(),
      message: z.string()
    })
  )
});

export const generalSettingsSchema = z.object({
  businessName: z.string().min(1),
  logoUrl: z.string().url().optional().or(z.literal("")),
  companyWebsite: z.string().url().optional().or(z.literal("")),
  customerFacingBaseUrl: z.string().url().optional().or(z.literal("")),
  defaultTimezone: z.string().min(1),
  currency: z.string().default("USD"),
  locale: z.string().default("en-US"),
  paymentsAccepted: z.array(z.string()).default([]),
  paymentsAcceptedOther: z.string().default(""),
  supportPhone: z.string().default(""),
  supportEmail: z.string().email().optional().or(z.literal("")),
  businessHours: z.array(businessHoursItemSchema).default([]),
  bookingLeadMinutes: z.number().int().min(0).default(120),
  cancellationPolicy: z.string().default("24 hours notice required."),
  cancellationFeeEnabled: z.boolean().default(false),
  cancellationPolicyMode: z.enum(["day_before_5pm", "custom"]).default("day_before_5pm"),
  cancellationPresetDaysBefore: z.number().int().min(1).max(2).default(1),
  cancellationPresetTime: z.string().default("17:00"),
  cancellationCutoffValue: z.number().int().min(1).default(47),
  cancellationCutoffUnit: z.enum(["hours", "days"]).default("hours"),
  cancellationPenaltyType: z.enum(["flat", "percentage"]).default("flat"),
  cancellationPenaltyValue: z.number().min(0).default(99),
  requireCancellationReason: z.boolean().default(true),
  applyCancellationFeeToCustomerInitiatedOnly: z.boolean().default(true),
  waiveCancellationFeeForFirstTimeNewCustomers: z.boolean().default(false),
  waiveCancellationFeeForFirstRecurringCancellation: z.boolean().default(false),
  requireInternalNoteWhenCancellationFeeWaived: z.boolean().default(false),
  taxRate: z.number().min(0).max(1).default(0.0825),
  showTaxOnBookingForm: z.boolean().default(true),
  invoicePrefix: z.string().default("BS")
});

export const bookingFormSettingsSchema = z.object({
  theme: z.enum(["sunrise", "ocean", "clean"]).default("sunrise"),
  visibleFields: z.array(z.string()).default(["name", "email", "phone", "address"]),
  requiredFields: z.array(z.string()).default(["name", "phone", "address"]),
  layout: z.enum(["single-step", "multi-step"]).default("multi-step"),
  stepOrder: z.array(z.string()).default(["service", "extras", "frequency", "calendar", "details", "review"]),
  enableExtrasStep: z.boolean().default(true),
  enableFrequencyStep: z.boolean().default(true),
  enableCalendarStep: z.boolean().default(true),
  enableLocationSelection: z.boolean().default(true),
  enablePromoCodes: z.boolean().default(true),
  enablePaymentPreference: z.boolean().default(false),
  enableTurnstile: z.boolean().default(true),
  arrivalFlexibility: z
    .object({
      enabled: z.boolean().default(false),
      startTime: timeSchema.default("07:30"),
      endTime: timeSchema.default("15:30"),
      discountType: z.enum(["fixed", "percentage"]).default("fixed"),
      discountValue: z.number().min(0).default(0),
      excludedWindowStartTimes: z.preprocess(coerceTimeArray, z.array(timeSchema).default(["07:30"]))
    })
    .default({
      enabled: false,
      startTime: "07:30",
      endTime: "15:30",
      discountType: "fixed",
      discountValue: 0,
      excludedWindowStartTimes: ["07:30"]
    }),
  customerSuppliesEquipment: z
    .object({
      enabled: z.boolean().default(false),
      discountAmount: z.number().min(0).default(0),
      requiredItems: z.preprocess(
        coerceStringArray,
        z.array(z.string().trim().min(1)).default(defaultCustomerSuppliesEquipmentItems)
      )
    })
    .default({
      enabled: false,
      discountAmount: 0,
      requiredItems: defaultCustomerSuppliesEquipmentItems
    }),
  depositRate: z.number().min(0).max(1).default(0.2),
  successRedirectUrl: z.string().url().optional().or(z.literal(""))
});

export const pricebookSettingsSchema = z.object({
  version: z.number().int().min(1).default(1),
  advancedRules: z.record(z.string(), z.unknown()).default({})
});

export const frequencySettingsSchema = z.object({
  recurringDiscountMode: z.enum(["all_bookings", "exclude_first_booking"]).default("all_bookings"),
  recurringFutureBookingLimit: z.number().int().min(1).default(12)
});

export const googleMapsApiSettingsSchema = z.object({
  apiKey: z.string().default(""),
  enableAddressAutocomplete: z.boolean().default(true)
});

export const invoiceSettingsSchema = z.object({
  invoicePrefix: z.string().min(1).default("INV"),
  nextInvoiceNumber: z.number().int().min(1).default(1001),
  defaultDueTerms: z.enum(["due_on_receipt"]).default("due_on_receipt"),
  defaultMemo: z.string().default("Thank you for choosing {{businessName}}."),
  defaultFooter: z.string().default("Please contact us with any invoice questions."),
  defaultPaymentInstructions: z.string().default("Pay securely online using the payment link in this email."),
  defaultEmailSubject: z.string().default("Invoice {{invoiceNumber}} from {{businessName}}"),
  defaultEmailBody: z
    .string()
    .default(
      "Hi {{customerName}},\n\nYour invoice {{invoiceNumber}} for {{bookingReference}} is attached. Amount due: {{amountDue}}.\n\nPay online: {{paymentUrl}}\n\nThank you,\n{{businessName}}"
  ),
  logoUrl: z.string().default(""),
  businessDisplayName: z.string().default(""),
  businessAddress: z.string().default(""),
  templateAccentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#123d32"),
  showCompanyContact: z.boolean().default(true),
  showBillTo: z.boolean().default(true),
  showServiceAddress: z.boolean().default(true),
  showAppointment: z.boolean().default(true),
  showMaidNames: z.boolean().default(true),
  showLineItems: z.boolean().default(true),
  showPayments: z.boolean().default(true),
  showMemo: z.boolean().default(true),
  showPaymentInstructions: z.boolean().default(true),
  showTermsLink: z.boolean().default(true),
  showFooter: z.boolean().default(true)
});

export const invoiceLineItemSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  description: z.string().default(""),
  quantity: z.number().min(0).default(1),
  unitPriceAmount: z.number().int().default(0),
  totalAmount: z.number().int().default(0)
});

export const invoiceDraftSnapshotSchema = z.object({
  businessName: z.string().default(""),
  businessLogoUrl: z.string().default(""),
  businessAddress: z.string().default(""),
  companyWebsite: z.string().default(""),
  supportPhone: z.string().default(""),
  supportEmail: z.string().default(""),
  termsUrl: z.string().default(""),
  customerName: z.string().default(""),
  customerEmail: z.string().default(""),
  customerPhone: z.string().default(""),
  billingAddress: z.string().default(""),
  serviceAddress: z.string().default(""),
  bookingReference: z.string().default(""),
  serviceDateLabel: z.string().default(""),
  appointmentLabel: z.string().default(""),
  serviceLabel: z.string().default(""),
  frequencyLabel: z.string().default(""),
  arrivalWindowLabel: z.string().default(""),
  maidNames: z.string().default(""),
  lineItems: z.array(invoiceLineItemSchema).default([]),
  subtotalAmount: z.number().int().default(0),
  discountAmount: z.number().int().default(0),
  taxAmount: z.number().int().default(0),
  totalAmount: z.number().int().default(0),
  amountPaid: z.number().int().default(0),
  amountDue: z.number().int().default(0),
  memo: z.string().default(""),
  footer: z.string().default(""),
  paymentInstructions: z.string().default("")
});

export const invoiceDraftUpdateSchema = z.object({
  issueDate: z.string().min(1),
  dueDate: z.string().min(1),
  snapshot: invoiceDraftSnapshotSchema
});

export const invoiceSendInputSchema = z.object({
  recipientEmail: z.string().email(),
  subject: z.string().min(1),
  body: z.string().min(1),
  attachPdf: z.boolean().default(true)
});

export const marketingEmailComplianceSettingsSchema = z.object({
  legalBusinessName: z.string().default(""),
  addressLine1: z.string().default(""),
  addressLine2: z.string().default(""),
  city: z.string().default(""),
  state: z.string().default(""),
  postalCode: z.string().default(""),
  country: z.string().default("US"),
  supportEmail: z.string().email().optional().or(z.literal("")),
  supportPhone: z.string().default("")
});

export const emailsSettingsSchema = z.object({
  senderName: z.string().min(1),
  senderEmail: z.string().email(),
  provider: z.enum(["resend", "smtp"]).default("resend"),
  resendApiKey: z.string().default(""),
  replyToEmail: z.string().email().optional().or(z.literal("")),
  marketingCompliance: marketingEmailComplianceSettingsSchema.default({
    legalBusinessName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "US",
    supportEmail: "",
    supportPhone: ""
  }),
  enabledTemplates: z.array(z.string()).default([
    "booking_confirmation",
    "booking_reminder",
    "booking_cancellation",
    "booking_rescheduled",
    "booking_extras_changed",
    "payment_receipt",
    "gift_card_delivery"
  ])
});

export const textMessagesSettingsSchema = z.object({
  provider: z.enum(["twilio"]).default("twilio"),
  quietHoursStart: timeSchema.default("20:00"),
  quietHoursEnd: timeSchema.default("08:00"),
  optInRequired: z.boolean().default(true),
  offerReminderOptInOnBookingForm: z.boolean().default(false),
  preselectReminderOptInOnBookingForm: z.boolean().default(false),
  bookingReminderOffsetsMinutes: z.preprocess(
    (value) => coercePositiveIntegerArray(value, [1440, 120]),
    z.array(z.number().int().min(1)).min(1)
  ),
  enabledTemplates: z.array(z.string()).default([
    "booking_confirmation",
    "booking_reminder",
    "on_the_way",
    "payment_reminder"
  ])
});

export const waitlistSettingsSchema = z.object({
  enabled: z.boolean().default(true),
  sendEmailNotifications: z.boolean().default(true),
  sendSmsNotifications: z.boolean().default(true),
  emailSubjectTemplate: z.string().default("{{businessName}}: a waitlist opening is available"),
  emailBodyTemplate: z
    .string()
    .default(
      "Hi {{customerName}},\n\nA matching {{serviceName}} opening is available for {{openingLabel}} at {{locationName}}.\nYour waitlist preference: {{preferredWindow}}.\n\nBook this opening: {{claimUrl}}\n\nOpen times are not held, so please book as soon as possible.\n\n{{supportLine}}"
    ),
  smsBodyTemplate: z
    .string()
    .default(
      "{{businessName}}: a {{serviceName}} waitlist opening is available for {{openingLabel}} at {{locationName}}. Claim it here: {{claimUrl}} Open times are not held, so please book as soon as possible."
    )
});

export const bookingFeedbackPhotoSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  contentType: z.string().default("application/octet-stream"),
  size: z.number().int().min(0).optional(),
  uploadedAt: z.string().nullable().default(null)
});

export const bookingFeedbackSchema = z.object({
  id: z.string().min(1),
  accountId: z.string().min(1),
  bookingId: z.string().min(1),
  customerId: z.string().min(1),
  requestSentAt: z.string().nullable().default(null),
  requestMessageLogId: z.string().nullable().default(null),
  clickedRating: z.number().int().min(1).max(5).nullable().default(null),
  clickedAt: z.string().nullable().default(null),
  overallRating: z.number().int().min(1).max(5).nullable().default(null),
  kitchenRating: z.number().int().min(1).max(5).nullable().default(null),
  bathroomsRating: z.number().int().min(1).max(5).nullable().default(null),
  bedroomsRating: z.number().int().min(1).max(5).nullable().default(null),
  livingAreasRating: z.number().int().min(1).max(5).nullable().default(null),
  comment: z.string().default(""),
  photos: z.array(bookingFeedbackPhotoSchema).default([]),
  viewedAt: z.string().nullable().default(null),
  submittedAt: z.string().nullable().default(null),
  lowRatingFlaggedAt: z.string().nullable().default(null),
  createdAt: z.string().nullable().default(null),
  updatedAt: z.string().nullable().default(null)
});

const bookingFeedbackOptionalRatingSchema = z.number().int().min(1).max(5).nullable().optional();

export const bookingFeedbackRatingClickSchema = z.object({
  rating: z.number().int().min(1).max(5)
});

export const bookingFeedbackUpdateInputSchema = z.object({
  overallRating: z.number().int().min(1).max(5),
  kitchenRating: bookingFeedbackOptionalRatingSchema,
  bathroomsRating: bookingFeedbackOptionalRatingSchema,
  bedroomsRating: bookingFeedbackOptionalRatingSchema,
  livingAreasRating: bookingFeedbackOptionalRatingSchema,
  comment: z.string().max(4000).default(""),
  photos: z.array(bookingFeedbackPhotoSchema).default([])
});

const nullableFeedbackRatingSchema = z.number().int().min(1).max(5).nullable().default(null);

export const staffFeedbackFeedItemSchema = z.object({
  bookingId: z.string().min(1),
  bookingPublicReference: z.string().default(""),
  serviceName: z.string().default(""),
  startsAt: z.string().default(""),
  endsAt: z.string().default(""),
  locationLabel: z.string().default(""),
  effectiveRating: nullableFeedbackRatingSchema,
  clickedRating: nullableFeedbackRatingSchema,
  overallRating: nullableFeedbackRatingSchema,
  kitchenRating: nullableFeedbackRatingSchema,
  bathroomsRating: nullableFeedbackRatingSchema,
  bedroomsRating: nullableFeedbackRatingSchema,
  livingAreasRating: nullableFeedbackRatingSchema,
  comment: z.string().default(""),
  photos: z.array(bookingFeedbackPhotoSchema).default([]),
  clickedAt: z.string().nullable().default(null),
  submittedAt: z.string().nullable().default(null),
  lowRatingFlaggedAt: z.string().nullable().default(null),
  showAreaRatings: z.boolean().default(false)
});

export const staffLoginAccessUserSchema = z.object({
  userId: z.string().min(1),
  email: z.string().email(),
  name: z.string().default(""),
  roles: z.array(roleSchema).default([]),
  isActive: z.boolean().default(true),
  lastLoginAt: z.string().nullable().default(null),
  staffId: z.string().nullable().default(null),
  mfaPhoneHint: z.string().default(""),
  mfaVerifiedAt: z.string().nullable().default(null),
  mfaSmsEnabled: z.boolean().default(false)
});

export const staffLoginAccessSchema = z.object({
  staffId: z.string().min(1),
  linkedUser: staffLoginAccessUserSchema.nullable().default(null),
  linkableUsers: z.array(staffLoginAccessUserSchema).default([])
});

export const staffLoginCreateInputSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  firstName: z.string().default(""),
  lastName: z.string().default(""),
  active: z.boolean().default(true)
});

export const staffLoginLinkInputSchema = z.object({
  userId: z.string().min(1)
});

export const staffLoginStatusInputSchema = z.object({
  active: z.boolean()
});

export const staffLoginPasswordResetInputSchema = z.object({
  password: z.string().min(8)
});

export const marketingSettingsSchema = z.object({
  leadSources: z.array(z.string()).default(["website", "google", "referral"]),
  captureUtm: z.boolean().default(true),
  enableAbandonedBookingFollowup: z.boolean().default(true),
  enableReviewRequests: z.boolean().default(true),
  reviewRequestDelayHours: z.number().int().min(0).max(720).default(24),
  reviewRequestLowRatingThreshold: z.number().int().min(1).max(5).default(3),
  notifyAssignedStaffOnFeedbackEmail: z.boolean().default(true),
  rebookingCouponCode: z.string().default("WELCOME10")
});

export const leadsSettingsSchema = z.object({
  allowedCaptureDomains: z.array(z.string()).default([]),
  defaultLeadSourceId: z.string().default(""),
  dedupeWindowDays: z.number().int().min(1).max(3650).default(90)
});

export const otherSettingsSchema = z.object({
  googleAnalyticsTrackingId: z.string().default(""),
  twilio: z
    .object({
      accountSid: z.string().default(""),
      authToken: z.string().default(""),
      phoneNumber: z.string().default("")
    })
    .default({
      accountSid: "",
      authToken: "",
      phoneNumber: ""
    }),
  googleCalendar: z
    .object({
      integrationMode: z.enum(["disabled", "authorize_bookingstack", "select_calendar"]).default("disabled"),
      selectedCalendarId: z.string().default(""),
      syncBookings: z.boolean().default(false)
    })
    .default({
      integrationMode: "disabled",
      selectedCalendarId: "",
      syncBookings: false
    }),
  zapier: z
    .object({
      enabled: z.boolean().default(false),
      webhookUrl: z.string().default("")
    })
    .default({
      enabled: false,
      webhookUrl: ""
    }),
  automatedTasks: z
    .object({
      enabled: z.boolean().default(false),
      notes: z.string().default("")
    })
    .default({
      enabled: false,
      notes: ""
    }),
  security: z
    .object({
      mfaRequiredForOfficeRoles: z.boolean().default(false)
    })
    .default({
      mfaRequiredForOfficeRoles: false
    }),
  featureFlags: z.record(z.string(), z.boolean()).default({}),
  customCss: z.string().default(""),
  dataRetentionDays: z.number().int().min(30).default(365),
  legalPages: z
    .object({
      termsUrl: z.string().url().optional().or(z.literal("")),
      privacyUrl: z.string().url().optional().or(z.literal("")),
      termsVersion: z.string().default(""),
      privacyVersion: z.string().default("")
    })
    .default({
      termsUrl: "",
      privacyUrl: "",
      termsVersion: "",
      privacyVersion: ""
    })
});

export const locationInputSchema = z.object({
  name: z.string().min(1),
  timezone: z.string().min(1),
  line1: z.string().default(""),
  line2: z.string().default(""),
  city: z.string().default(""),
  state: z.string().default(""),
  postalCode: z.string().default(""),
  country: z.string().default("US"),
  bookingLeadMinutes: z.number().int().min(0).default(120),
  bookingWindowDays: z.number().int().min(1).default(60),
  businessHours: z.array(businessHoursItemSchema).default([]),
  serviceArea: z.array(z.string()).default([]),
  serviceAreaRules: z
    .array(
      z.object({
        zipCode: z.string().regex(/^\d{5}$/),
        earliestStartTime: timeSchema.optional().or(z.literal("")),
        latestStartTime: timeSchema.optional().or(z.literal("")),
        allowedWeekdays: z.array(weekdaySchema).default([]),
        active: z.boolean().default(true),
        note: z.string().default("")
      })
    )
    .default([]),
  active: z.boolean().default(true)
});

export const serviceInputSchema = z.object({
  name: z.string().min(1),
  icon: z.string().default(""),
  category: z.string().default("cleaning"),
  description: z.string().default(""),
  displayOrder: z.number().int().min(0).default(0),
  durationMinutes: z.number().int().min(30).default(180),
  basePriceAmount: moneyAmountSchema.default(12000),
  bufferBeforeMinutes: z.number().int().min(0).default(0),
  bufferAfterMinutes: z.number().int().min(0).default(30),
  pricingRules: z.record(z.string(), z.unknown()).default({}),
  options: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string(),
        priceDeltaAmount: moneyAmountSchema,
        durationDeltaMinutes: z.number().int().default(0)
      })
    )
    .default([]),
  assignedExtraIds: z.array(z.string()).default([]),
  availableLocationIds: z.array(z.string()).default([]),
  allowAllActiveFrequencyRules: z.boolean().default(true),
  allowedFrequencyRuleIds: z.array(z.string()).default([]),
  defaultCalendarProfileId: z.string().optional().or(z.literal("")),
  extraVisibilityRules: z.record(z.string(), z.unknown()).default({}),
  staffOnly: z.boolean().default(false),
  active: z.boolean().default(true)
});

export const carpetShampooingUnitCodeSchema = z.enum(["rooms", "hallways", "steps", "area_rugs"]);

export const carpetShampooingUnitSchema = z.object({
  code: carpetShampooingUnitCodeSchema,
  label: z.string().min(1),
  priceDeltaAmount: moneyAmountSchema.default(0),
  durationDeltaMinutes: z.number().int().min(0).default(0),
  maxQuantity: z.number().int().min(0).default(0),
  displayOrder: z.number().int().min(0).default(0),
  active: z.boolean().default(true)
});

export const carpetShampooingConfigSchema = z.object({
  enabled: z.boolean().default(false),
  minimumOrderAmount: moneyAmountSchema.default(0),
  units: z.array(carpetShampooingUnitSchema).default([])
});

export const extraInputSchema = z.object({
  name: z.string().min(1),
  description: z.string().default(""),
  icon: z.string().default(""),
  displayOrder: z.number().int().min(0).default(0),
  quantityBased: z.boolean().default(false),
  quantityLabel: z.string().default(""),
  quantityUnitLabel: z.string().default(""),
  quantityAdminDetailKey: z.string().default(""),
  minQuantity: z.number().int().min(1).default(1),
  maxQuantity: z.number().int().min(1).default(10),
  priceDeltaAmount: moneyAmountSchema.default(0),
  durationDeltaMinutes: z.number().int().min(0).default(0),
  extraPricingRules: z
    .array(
      z.object({
        id: z.string().optional(),
        serviceIds: z.array(z.string()).default([]),
        squareFootageFrom: z.string().default(""),
        squareFootageTo: z.string().default(""),
        bedroomsFrom: z.string().default(""),
        bedroomsTo: z.string().default(""),
        levelsFrom: z.string().default(""),
        levelsTo: z.string().default(""),
        priceDeltaAmount: moneyAmountSchema.default(0),
        durationDeltaMinutes: z.number().int().min(0).default(0),
        displayOrder: z.number().int().min(0).default(0),
        active: z.boolean().default(true)
      })
    )
    .default([]),
  carpetShampooingConfig: carpetShampooingConfigSchema.default({
    enabled: false,
    minimumOrderAmount: 0,
    units: []
  }),
  assignedServiceIds: z.array(z.string()).default([]),
  excludeFromFrequencyDiscount: z.boolean().default(false),
  excludeFromPromoDiscount: z.boolean().default(false),
  recurringPolicy: z.enum(["every_booking", "first_booking_only"]).default("every_booking"),
  active: z.boolean().default(true)
});

export const priceAdjusterInputSchema = z
  .object({
    direction: z.enum(["increase", "decrease"]).default("increase"),
    percent: z.number().min(0).max(500),
    roundToNearestDollar: z.boolean().default(false),
    serviceIds: z.array(z.string()).default([]),
    extraIds: z.array(z.string()).default([])
  })
  .superRefine((value, context) => {
    if (value.direction === "decrease" && value.percent > 100) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["percent"],
        message: "Decrease percent cannot be greater than 100."
      });
    }
  });

export const priceAdjusterItemSchema = z.object({
  entityType: z.enum(["service", "extra"]),
  id: z.string(),
  name: z.string(),
  oldPriceAmount: moneyAmountSchema,
  newPriceAmount: moneyAmountSchema,
  deltaAmount: z.number().int(),
  skipped: z.boolean(),
  skipReason: z.string().default("")
});

export const priceAdjusterResponseSchema = z.object({
  items: z.array(priceAdjusterItemSchema),
  changedCount: z.number().int().min(0),
  skippedCount: z.number().int().min(0)
});

export const bookingSpotDisplayModeSchema = z.enum(["specific_times", "arrival_windows", "mixed"]);

export const bookingSpotDailyScheduleItemSchema = z.object({
  day: weekdaySchema,
  enabled: z.boolean().default(true),
  start: timeSchema,
  end: timeSchema,
  breakStart: timeSchema.optional().or(z.literal("")),
  breakEnd: timeSchema.optional().or(z.literal(""))
});

export const bookingSpotHolidayOverrideSchema = z.object({
  date: isoDateSchema,
  closed: z.boolean().default(true),
  start: timeSchema.optional().or(z.literal("")),
  end: timeSchema.optional().or(z.literal("")),
  note: z.string().default("")
});

export const bookingSpotCapacityRuleSchema = z.object({
  day: weekdaySchema,
  start: timeSchema,
  end: timeSchema,
  maxJobs: z.number().int().min(1).default(1),
  linkedServiceIds: z.array(z.string()).default([])
});

export const bookingSpotDateOverrideSchema = z.object({
  date: isoDateSchema,
  closed: z.boolean().default(false),
  start: timeSchema.optional().or(z.literal("")),
  end: timeSchema.optional().or(z.literal("")),
  maxJobs: z.number().int().min(1).optional(),
  note: z.string().default("")
});

export const bookingSpotAvailabilityModeSchema = z.enum(["manual", "team_based"]);

export const bookingSpotOutsideAvailabilityPolicySchema = z.enum([
  "count_against_previous_slot",
  "count_against_matching_range",
  "do_not_count"
]);

export const bookingSpotDurationRoundingModeSchema = z.enum([
  "round_up_to_next_slot",
  "exact_minutes"
]);

export const bookingSpotInputSchema = z.object({
  name: z.string().min(1),
  locationId: z.string().min(1),
  linkedServiceIds: z.array(z.string()).default([]),
  availabilityMode: bookingSpotAvailabilityModeSchema.default("manual"),
  teamSize: z.number().int().min(1).default(2),
  fallbackTeamSizes: z.array(z.number().int().min(1).max(4)).default([]),
  allowSingleStaffFallback: z.boolean().default(true),
  teamBasedCapacityCap: z.number().int().min(1).optional(),
  displayMode: bookingSpotDisplayModeSchema.default("specific_times"),
  capacity: z.number().int().min(1).default(1),
  intervalMinutes: z.number().int().min(15).default(30),
  operatingHours: z.array(businessHoursItemSchema).default([]),
  blackoutDates: z.array(isoDateSchema).default([]),
  dailySchedule: z.array(bookingSpotDailyScheduleItemSchema).default([]),
  holidayOverrides: z.array(bookingSpotHolidayOverrideSchema).default([]),
  capacityRules: z.array(bookingSpotCapacityRuleSchema).default([]),
  dateOverrides: z.array(bookingSpotDateOverrideSchema).default([]),
  durationPolicyEnabled: z.boolean().default(true),
  allowDurationOverlapIntoNextSpot: z.boolean().default(false),
  blockIfDurationExceedsAvailableTime: z.boolean().default(true),
  outsideNormalAvailabilityPolicy: bookingSpotOutsideAvailabilityPolicySchema.default("count_against_previous_slot"),
  durationRoundingMode: bookingSpotDurationRoundingModeSchema.default("round_up_to_next_slot"),
  maxSimultaneousBookings: z.number().int().min(1).default(1),
  active: z.boolean().default(true)
});

export const calendarProfileInputSchema = z.object({
  name: z.string().min(1),
  description: z.string().default(""),
  linkedLocationIds: z.array(z.string()).default([]),
  linkedBookingSpotIds: z.array(z.string()).default([]),
  allowedWeekdays: z.array(weekdaySchema).default([]),
  blackoutDates: z.array(isoDateSchema).default([]),
  advanceNoticeMinutes: z.number().int().min(0).default(120),
  bookingWindowDays: z.number().int().min(1).default(60),
  active: z.boolean().default(true)
});

export const frequencyRuleInputSchema = z.object({
  name: z.string().min(1),
  code: z.string().default(""),
  description: z.string().default(""),
  cadenceLabel: z.string().default(""),
  applicableServiceIds: z.array(z.string()).default([]),
  discountType: z.enum(["none", "fixed", "percentage"]).default("none"),
  discountValue: z.number().min(0).default(0),
  displayOrder: z.number().int().min(0).default(0),
  isDefault: z.boolean().default(false),
  active: z.boolean().default(true)
});

export const promotionInputSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  discountType: z.enum(["fixed", "percentage"]).default("percentage"),
  discountValue: z.number().int().min(0),
  applicableServiceIds: z.array(z.string()).default([]),
  startsAt: isoDateTimeSchema.optional(),
  endsAt: isoDateTimeSchema.optional(),
  serviceDateStart: isoDateSchema.optional(),
  serviceDateEnd: isoDateSchema.optional(),
  validWeekdays: z.array(weekdaySchema).default([]),
  usageLimit: z.number().int().min(1).default(100),
  firstTimeOnly: z.boolean().default(false),
  minimumSpendAmount: moneyAmountSchema.default(0),
  sourceRestriction: z.string().default(""),
  active: z.boolean().default(true)
});

export const serviceScenarioInputSchema = z.object({
  baseServiceId: z.string().min(1),
  name: z.string().min(1),
  code: z.string().min(1),
  displayOrder: z.number().int().min(0).default(0),
  pricingMode: z.enum(["exact", "approximate", "manual_review"]).default("exact"),
  questionProfile: z
    .enum(["residential", "office_commercial", "building_common_areas", "carpet_standalone"])
    .optional()
    .or(z.literal("")),
  remodelProfile: z
    .enum(["residential", "office_commercial", "building_common_areas", "none"])
    .optional()
    .or(z.literal("")),
  extraFilterProfile: z
    .enum(["residential", "office_commercial", "building_common_areas", "carpet_standalone"])
    .optional()
    .or(z.literal("")),
  customerNote: z.string().default(""),
  internalNote: z.string().default(""),
  squareFootageMin: z.number().int().min(0).nullable().optional(),
  squareFootageMax: z.number().int().min(0).nullable().optional(),
  priority: z.number().int().min(0).default(0),
  showFrequency: z.boolean().default(true),
  showHomeDetails: z.boolean().default(true),
  showConditionSection: z.boolean().default(true),
  showRemodelingSection: z.boolean().default(true),
  showExtras: z.boolean().default(true),
  showTip: z.boolean().default(true),
  allowedSkipAreas: z.array(z.string()).default([]),
  visibleExtraIds: z.array(z.string()).default([]),
  remodelingPricingRules: z
    .array(
      z.object({
        optionCode: z.string().min(1),
        optionLabel: z.string().min(1),
        hourlyRatePerMaidHour: z.number().min(0).default(0),
        minimumMaidCount: z.number().int().min(1).default(2),
        minimumHours: z.number().min(0).default(0),
        active: z.boolean().default(true)
      })
    )
    .default([]),
  defaultFrequencyRuleId: z.string().optional().or(z.literal("")),
  active: z.boolean().default(true)
});

export const staffInputSchema = z.object({
  firstName: z.string().default(""),
  lastName: z.string().default(""),
  staffTypeId: z.string().optional().or(z.literal("")),
  workerGroup: z.string().default(""),
  code: z.string().default(""),
  name: z.string().default(""),
  businessName: z.string().default(""),
  address1: z.string().default(""),
  address2: z.string().default(""),
  city: z.string().default(""),
  state: z.string().default(""),
  postalCode: z.string().default(""),
  country: z.string().default("US"),
  phoneNumber: z.string().default(""),
  emergencyContact: z.string().default(""),
  email: z.string().email(),
  phone: z.string().default(""),
  dateOfBirth: z.string().default(""),
  hireDate: z.string().default(""),
  teamLeader: z.boolean().default(false),
  englishSpeaking: z.boolean().default(false),
  availabilityDays: z.array(z.string()).default([]),
  availabilityText: z.string().default(""),
  availabilityStartTime: z.string().default("08:00"),
  availabilityEndTime: z.string().default("17:00"),
  preferredTeamMemberId: z.string().optional().or(z.literal("")),
  cannotWorkWithStaffIds: z.array(z.string()).default([]),
  tagIds: z.array(z.string()).default([]),
  notes: z.string().default(""),
  payType: z.enum(["hourly", "flat-fee", "revenue-share"]).default("hourly"),
  compensation: z.number().min(0).default(0),
  bankRoutingNumber: z.string().default(""),
  bankAccountNumber: z.string().default(""),
  driverLicenseNumber: z.string().default(""),
  licensePlate: z.string().default(""),
  vehicleYear: z.string().default(""),
  vehicleMake: z.string().default(""),
  vehicleModel: z.string().default(""),
  vehicleColor: z.string().default(""),
  odometer: z.string().default(""),
  equipmentRecords: z
    .array(
      z.object({
        assignedDate: z.string().default(""),
        equipmentName: z.string().default("")
      })
    )
    .default([]),
  shirtSize: z.string().default(""),
  isArchived: z.boolean().default(false),
  salaryMethodId: z.string().optional().nullable(),
  serviceSkillIds: z.array(z.string()).default([]),
  assignedLocationIds: z.array(z.string()).default([]),
  paySettings: z.record(z.string(), z.unknown()).default({}),
  commissionRules: z.record(z.string(), z.unknown()).default({}),
  active: z.boolean().default(true)
});

export const staffTypeInputSchema = z.object({
  name: z.string().min(1),
  code: z.string().min(1),
  directoryCategory: z.enum(["maids", "drivers", "office"]).default("maids"),
  displayOrder: z.number().int().min(0).default(0),
  active: z.boolean().default(true)
});

export const staffTimeOffInputSchema = z
  .object({
    staffId: z.string().min(1),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    reason: z.string().default("")
  })
  .refine((value) => value.endDate >= value.startDate, {
    message: "End date must be on or after start date",
    path: ["endDate"]
  });

export const schedulerDayAvailabilityStatusValues = ["available", "off"] as const;
export const schedulerTeamStateValues = ["ready", "needs_review"] as const;
export const schedulerTeamBuildSourceValues = ["bootstrap", "manual"] as const;
export const schedulerAssignmentModeValues = ["auto", "manual"] as const;
export const schedulerUnassignedReasonCodeValues = [
  "no_ready_team",
  "service_ineligible",
  "location_ineligible",
  "team_size_preference_not_met",
  "overlap_conflict",
  "driver_missing",
  "team_deleted",
  "manual_unassigned",
  "manual_assignment_invalid"
] as const;

export const schedulerHomeTeamInputSchema = z.object({
  locationId: z.string().min(1),
  name: z.string().min(1),
  defaultDriverStaffId: z.string().optional().nullable(),
  memberStaffIds: z.array(z.string().min(1)).min(1),
  leadStaffId: z.string().optional().nullable(),
  active: z.boolean().default(true)
});

export const schedulerDayQuerySchema = z.object({
  locationId: z.string().min(1),
  date: isoDateSchema
});

export const schedulerDayBootstrapInputSchema = schedulerDayQuerySchema.extend({
  mode: z.enum(["from_home_teams"]).default("from_home_teams")
});

export const schedulerDayAutoAssignInputSchema = schedulerDayQuerySchema.extend({
  preserveLocked: z.boolean().default(true),
  clearUnlocked: z.boolean().default(true)
});

export const schedulerDayStaffOverrideEntrySchema = z.object({
  staffId: z.string().min(1),
  availabilityStatus: z.enum(schedulerDayAvailabilityStatusValues),
  note: z.string().default("")
});

export const schedulerDayStaffOverridesInputSchema = schedulerDayQuerySchema.extend({
  overrides: z.array(schedulerDayStaffOverrideEntrySchema).default([])
});

export const schedulerDayTeamInputSchema = schedulerDayQuerySchema.extend({
  name: z.string().min(1),
  memberStaffIds: z.array(z.string().min(1)).min(1),
  driverStaffId: z.string().optional().nullable(),
  sourceMaidTeamId: z.string().optional().nullable(),
  targetTeamSize: z.number().int().min(1).optional().nullable(),
  teamState: z.enum(schedulerTeamStateValues).optional(),
  routeStartTime: z.string().optional().nullable(),
  notes: z.string().default("")
});

export const schedulerDayTeamUpdateInputSchema = z.object({
  name: z.string().min(1).optional(),
  memberStaffIds: z.array(z.string().min(1)).min(1),
  driverStaffId: z.string().optional().nullable(),
  targetTeamSize: z.number().int().min(1).optional().nullable(),
  teamState: z.enum(schedulerTeamStateValues).optional(),
  routeStartTime: z.string().optional().nullable(),
  notes: z.string().default("")
});

export const schedulerDayBookingAssignmentUpdateSchema = z.object({
  scheduleDayTeamId: z.string().nullable(),
  isLocked: z.boolean().optional(),
  sequenceIndex: z.number().int().min(0).optional().nullable(),
  note: z.string().default("")
});

export const schedulerHomeTeamMemberSchema = z.object({
  staffId: z.string().min(1),
  name: z.string().default(""),
  isLead: z.boolean().default(false),
  positionOrder: z.number().int().min(0).default(0)
});

export const schedulerHomeTeamSchema = z.object({
  id: z.string().min(1),
  locationId: z.string().min(1),
  name: z.string().min(1),
  defaultDriverStaffId: z.string().nullable(),
  defaultDriverName: z.string().nullable().default(null),
  active: z.boolean().default(true),
  members: z.array(schedulerHomeTeamMemberSchema).default([])
});

export const schedulerDayAvailableStaffSchema = z.object({
  staffId: z.string().min(1),
  name: z.string().default(""),
  homeTeamId: z.string().nullable().default(null),
  defaultTeamIds: z.array(z.string()).default([]),
  serviceIds: z.array(z.string()).default([]),
  locationIds: z.array(z.string()).default([]),
  source: z.enum(["weekly_availability", "manual_override"]).default("weekly_availability")
});

export const schedulerDayOffStaffSchema = z.object({
  staffId: z.string().min(1),
  name: z.string().default(""),
  reason: z.enum(["time_off", "manual_off", "not_scheduled_today"])
});

export const schedulerDayTeamSchema = z.object({
  id: z.string().min(1),
  sourceMaidTeamId: z.string().nullable(),
  name: z.string().min(1),
  driverStaffId: z.string().nullable(),
  driverName: z.string().nullable().default(null),
  memberStaffIds: z.array(z.string()).default([]),
  memberNames: z.array(z.string()).default([]),
  targetTeamSize: z.number().int().min(1),
  actualTeamSize: z.number().int().min(0),
  teamState: z.enum(schedulerTeamStateValues),
  buildSource: z.enum(schedulerTeamBuildSourceValues).default("manual"),
  assignedBookingCount: z.number().int().min(0),
  routeStartTime: z.string().nullable().default(null),
  routePublishedAt: z.string().nullable().default(null),
  notes: z.string().default("")
});

export const schedulerBoardBookingSchema = z.object({
  bookingId: z.string().min(1),
  publicReference: z.string().default(""),
  customerName: z.string().default(""),
  status: z.string().default(""),
  startsAt: z.string(),
  endsAt: z.string(),
  arrivalWindowLabel: z.string().default(""),
  serviceAddress: z.string().default(""),
  postalCode: z.string().default(""),
  serviceIds: z.array(z.string()).default([]),
  locationId: z.string().min(1),
  assignment: z.object({
    scheduleDayTeamId: z.string().nullable(),
    assignmentMode: z.enum(schedulerAssignmentModeValues),
    isLocked: z.boolean(),
    sequenceIndex: z.number().int().nullable(),
    plannedStartAt: z.string().nullable().default(null),
    plannedEndAt: z.string().nullable().default(null),
    manualNote: z.string().default(""),
    unassignedReasonCode: z.enum(schedulerUnassignedReasonCodeValues).nullable(),
    unassignedReasonText: z.string().nullable()
  })
});

export const schedulerBookingVisibilitySchema = z.object({
  locationId: z.string().min(1),
  date: isoDateSchema,
  includedStatuses: z.array(z.string()).default(["pending", "confirmed", "rescheduled"]),
  visibleCount: z.number().int().min(0),
  hiddenSameDate: z.object({
    otherLocationCount: z.number().int().min(0),
    excludedStatusCount: z.number().int().min(0),
    missingLocationCount: z.number().int().min(0)
  })
});

export const schedulerDayBoardSchema = z.object({
  day: z.object({
    id: z.string().min(1),
    locationId: z.string().min(1),
    date: isoDateSchema,
    warnings: z.array(z.string()).default([])
  }),
  availableMaids: z.array(schedulerDayAvailableStaffSchema).default([]),
  availableDrivers: z.array(schedulerDayAvailableStaffSchema).default([]),
  offStaff: z.array(schedulerDayOffStaffSchema).default([]),
  homeTeams: z.array(schedulerHomeTeamSchema).default([]),
  teams: z.array(schedulerDayTeamSchema).default([]),
  bookings: z.array(schedulerBoardBookingSchema).default([]),
  bookingVisibility: schedulerBookingVisibilitySchema.optional(),
  syncWarnings: z.array(z.string()).default([])
});

export const driverRouteQuerySchema = z.object({
  date: isoDateSchema,
  locationId: z.string().min(1).optional()
});

export const driverRouteAccessInformationSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1)
});

export const driverRouteOnWayNoticeSchema = z.object({
  messageLogId: z.string().min(1),
  status: z.string().default(""),
  minutes: z.number().int().min(1).nullable().default(null),
  sentAt: z.string().nullable().default(null)
});

export const driverRouteStaffMemberSchema = z.object({
  staffId: z.string().min(1),
  name: z.string().default(""),
  roleLabel: z.string().default("primary"),
  isLead: z.boolean().default(false)
});

export const driverRouteStopSchema = z.object({
  bookingId: z.string().min(1),
  publicReference: z.string().default(""),
  status: z.string().default(""),
  customerId: z.string().min(1),
  customerName: z.string().default(""),
  customerPhone: z.string().default(""),
  serviceAddress: z.string().default(""),
  startsAt: z.string(),
  endsAt: z.string(),
  plannedStartAt: z.string().nullable().default(null),
  plannedEndAt: z.string().nullable().default(null),
  cleaningType: z.string().default(""),
  serviceSummary: z.string().default(""),
  extras: z.array(z.string()).default([]),
  accessInformation: z.array(driverRouteAccessInformationSchema).default([]),
  assignedMaids: z.array(driverRouteStaffMemberSchema).default([]),
  estimatedLaborHours: z.number().min(0).nullable().default(null),
  driverNotes: z.string().default(""),
  onWayNotice: driverRouteOnWayNoticeSchema.nullable().default(null)
});

export const driverRouteSchema = z.object({
  teamId: z.string().min(1),
  teamName: z.string().default(""),
  locationId: z.string().min(1),
  locationName: z.string().default(""),
  timeZone: z.string().default("America/Chicago"),
  routeStartTime: z.string().nullable().default(null),
  routePublishedAt: z.string().min(1),
  driverStaffId: z.string().min(1),
  driverName: z.string().default(""),
  memberNames: z.array(z.string()).default([]),
  stops: z.array(driverRouteStopSchema).default([])
});

export const driverRouteResponseSchema = z.object({
  date: isoDateSchema,
  routes: z.array(driverRouteSchema).default([]),
  availableMaids: z.array(driverRouteStaffMemberSchema).default([]),
  canManageRouteStaffing: z.boolean().default(false),
  generatedAt: z.string()
});

export const driverOnWayInputSchema = z.object({
  minutes: z.number().int().min(1).max(180)
});

export const driverFieldWorkUpdateInputSchema = z.object({
  staffIds: z.array(z.string().min(1)).max(12).default([]),
  estimatedLaborHours: z.number().min(0).max(1000).nullable().default(null),
  notes: z.string().max(2000).default("")
});

export const driverFieldWorkUpdateResultSchema = z.object({
  ok: z.boolean(),
  bookingId: z.string().min(1),
  assignedMaids: z.array(driverRouteStaffMemberSchema).default([]),
  estimatedLaborHours: z.number().min(0).nullable().default(null),
  driverNotes: z.string().default(""),
  updatedAt: z.string()
});

export const driverOnWayResultSchema = z.object({
  ok: z.boolean(),
  bookingId: z.string().min(1),
  minutes: z.number().int().min(1),
  sid: z.string().optional(),
  messageLogId: z.string().optional(),
  sentAt: z.string().optional()
});

export const tagInputSchema = z.object({
  entityType: z.enum(["customer", "booking", "promotion", "service", "staff"]).default("customer"),
  name: z.string().min(1),
  displayOrder: z.number().int().min(0).default(0),
  directoryCategory: z.enum(["all", "maids", "drivers", "office"]).default("all"),
  color: z.string().default("#1D6B57"),
  active: z.boolean().default(true)
});

export const salaryMethodInputSchema = z.object({
  name: z.string().min(1),
  methodType: z.enum([
    "hourly",
    "fixed_per_booking",
    "percentage_commission",
    "service_rate",
    "bonus_plus_base",
    "team_split"
  ]),
  config: z.record(z.string(), z.unknown()).default({}),
  active: z.boolean().default(true)
});

export const notificationEventValues = [
  "booking_confirmation",
  "admin_new_booking",
  "booking_reminder",
  "booking_cancellation",
  "booking_rescheduled",
  "booking_extras_changed"
] as const;
export const notificationChannelValues = ["email", "sms"] as const;
export const notificationRecipientValues = ["customer", "admin", "office"] as const;

export const notificationRuleInputSchema = z.object({
  name: z.string().min(1),
  eventKey: z.enum(notificationEventValues).default("booking_confirmation"),
  channel: z.enum(notificationChannelValues).default("email"),
  recipientType: z.enum(notificationRecipientValues).default("customer"),
  templateKey: z.string().min(1),
  timingMode: z.enum(["immediate"]).default("immediate"),
  timingOffsetMinutes: z.number().int().min(0).default(0),
  conditions: z.record(z.string(), z.unknown()).default({}),
  displayOrder: z.number().int().min(0).default(0),
  active: z.boolean().default(true)
});

export const notificationJobSchema = z.object({
  accountId: z.string().min(1),
  eventKey: z.enum(notificationEventValues),
  channel: z.enum(notificationChannelValues),
  recipientType: z.enum(notificationRecipientValues),
  recipient: z.string().min(1),
  templateKey: z.string().min(1),
  entityType: z.enum(["booking", "payment", "customer"]),
  entityId: z.string().min(1),
  dedupeKey: z.string().min(1),
  metadata: z
    .object({
      reminderOffsetMinutes: z.number().int().min(1).optional(),
      bookingStartsAtSnapshot: isoDateTimeSchema.optional(),
      scheduledSendAt: isoDateTimeSchema.optional(),
      originalStartsAt: isoDateTimeSchema.optional(),
      updatedStartsAt: isoDateTimeSchema.optional(),
      originalArrivalWindowLabel: z.string().optional(),
      updatedArrivalWindowLabel: z.string().optional(),
      previousExtrasSummary: z.string().optional(),
      currentExtrasSummary: z.string().optional(),
      addedExtras: z.array(z.string()).optional(),
      removedExtras: z.array(z.string()).optional(),
      changeSource: z.enum(["admin", "public_manage", "customer_portal"]).optional()
    })
    .optional()
});

export const paymentWebhookJobSchema = z.object({
  eventId: z.string().min(1)
});

export const communicationTemplateTypeValues = ["transactional", "marketing"] as const;
export const marketingSubscriberStatusValues = ["subscribed", "unsubscribed", "suppressed"] as const;
export const communicationMessageKindValues = ["transactional", "marketing"] as const;
export const emailCampaignStatusValues = ["draft", "scheduled", "sending", "sent", "sent_with_errors", "cancelled"] as const;
export const emailCampaignAudienceModeValues = ["subscribers", "customers", "all_contacts"] as const;
export const emailAutomationTriggerValues = [
  "days_since_last_completed_booking",
  "days_since_cancelled_booking",
  "days_since_no_show",
  "first_completed_booking",
  "service_upsell_after_completed_booking"
] as const;
export const emailAutomationDelayUnitValues = ["minutes", "hours", "days"] as const;
export const emailAutomationEnrollmentPolicyValues = ["all_eligible", "future_only"] as const;
export const generatedAutomationOfferEligibilityValues = ["newsletter_first_booking", "customer_winback"] as const;

export const generatedAutomationOfferInputSchema = z.object({
  enabled: z.boolean().default(false),
  discountType: z.enum(["percentage"]).default("percentage"),
  discountValue: z.number().int().min(1).max(100).default(10),
  codePrefix: z.string().max(24).default(""),
  expirationDays: z.number().int().min(1).max(365).default(7),
  eligibilityMode: z.enum(generatedAutomationOfferEligibilityValues).default("newsletter_first_booking")
});

export const emailLayoutInputSchema = z.object({
  name: z.string().min(1),
  primaryColor: z.string().default("#c65f3f"),
  textColor: z.string().default("#18261f"),
  logoUrl: z.string().default(""),
  heroImageUrl: z.string().default(""),
  htmlShell: z.string().min(1),
  footerHtml: z.string().default(""),
  isDefault: z.boolean().default(false),
  active: z.boolean().default(true)
});

export const emailTemplateInputSchema = z.object({
  name: z.string().min(1),
  key: z.string().min(1),
  templateType: z.enum(communicationTemplateTypeValues).default("marketing"),
  layoutId: z.string().optional().or(z.literal("")),
  category: z.string().default("general"),
  subjectTemplate: z.string().min(1),
  preheader: z.string().default(""),
  bodyHtml: z.string().min(1),
  bodyText: z.string().default(""),
  editor: z.record(z.string(), z.unknown()).default({}),
  fromNameOverride: z.string().default(""),
  fromEmailOverride: z.string().default(""),
  replyToOverride: z.string().default(""),
  active: z.boolean().default(true)
});

export const marketingSubscriberInputSchema = z.object({
  email: z.string().email(),
  firstName: z.string().default(""),
  lastName: z.string().default(""),
  status: z.enum(marketingSubscriberStatusValues).default("subscribed"),
  source: z.string().default("admin_manual"),
  sourceDetails: z.record(z.string(), z.unknown()).default({}),
  tags: z.array(z.string()).default([])
});

export const marketingSubscriberImportSchema = z.object({
  text: z.string().min(1),
  source: z.string().default("admin_import")
});

export const newsletterSubscribeInputSchema = z.object({
  email: z.string().email(),
  firstName: z.string().default(""),
  lastName: z.string().default(""),
  source: z.string().default("website_newsletter")
});

export const emailTemplatePreviewSchema = z.object({
  sampleRecipientEmail: z.string().email().optional().or(z.literal("")),
  variables: z.record(z.string(), z.unknown()).default({})
});

export const emailTemplateTestSchema = z.object({
  to: z.string().email(),
  sampleRecipientEmail: z.string().email().optional().or(z.literal("")),
  variables: z.record(z.string(), z.unknown()).default({})
});

export const emailCampaignInputSchema = z.object({
  name: z.string().min(1),
  templateId: z.string().min(1),
  audienceMode: z.enum(emailCampaignAudienceModeValues).default("subscribers"),
  audienceFilters: z.record(z.string(), z.unknown()).default({})
});

export const emailCampaignScheduleSchema = z.object({
  scheduledFor: z.string().datetime({ offset: true })
});

export const communicationCampaignJobSchema = z.object({
  accountId: z.string().min(1),
  campaignId: z.string().min(1),
  campaignRecipientId: z.string().min(1)
});

export const emailAutomationAudienceFiltersSchema = z.object({
  tagIds: z.array(z.string()).default([]),
  serviceFrequencies: z.array(z.string()).default([]),
  locationIds: z.array(z.string()).default([]),
  sourceServiceIds: z.array(z.string()).default([]),
  targetServiceIds: z.array(z.string()).default([])
});

export const emailAutomationStepTemplateIdsSchema = z.record(z.string(), z.string().min(1)).default({});

export const emailAutomationStepInputSchema = z.object({
  id: z.string().default(""),
  delayValue: z.number().int().min(1).max(3650),
  delayUnit: z.enum(emailAutomationDelayUnitValues).default("days"),
  templateId: z.string().default(""),
  generatedOffer: generatedAutomationOfferInputSchema.default({
    enabled: false,
    discountType: "percentage",
    discountValue: 10,
    codePrefix: "",
    expirationDays: 7,
    eligibilityMode: "newsletter_first_booking"
  })
});

export const emailAutomationInputSchema = z.object({
  name: z.string().min(1),
  templateId: z.string().min(1),
  triggerType: z.enum(emailAutomationTriggerValues).default("days_since_last_completed_booking"),
  delayDays: z.number().int().min(1).max(3650).default(90),
  followUpDelays: z.array(z.number().int().min(1).max(3650)).default([]),
  stepTemplateIds: emailAutomationStepTemplateIdsSchema,
  steps: z.array(emailAutomationStepInputSchema).default([]),
  enrollmentPolicy: z.enum(emailAutomationEnrollmentPolicyValues).default("all_eligible"),
  audienceFilters: emailAutomationAudienceFiltersSchema.default({
    tagIds: [],
    serviceFrequencies: [],
    locationIds: [],
    sourceServiceIds: [],
    targetServiceIds: []
  }),
  active: z.boolean().default(true)
});

export const communicationAutomationJobSchema = z.object({
  accountId: z.string().min(1),
  automationId: z.string().min(1),
  automationEnrollmentId: z.string().min(1)
});

export const leadStatusValues = ["new", "contacted", "estimate_sent", "won", "lost"] as const;
export const leadAutomationDelayUnitValues = ["minutes", "hours", "days"] as const;
export const leadAutomationChannelValues = ["email", "sms"] as const;

export const marketingSourceInputSchema = z.object({
  name: z.string().min(1),
  slug: z.string().default(""),
  channel: z.string().default("web"),
  active: z.boolean().default(true),
  config: z.record(z.string(), z.unknown()).default({})
});

const leadContactShape = {
  firstName: z.string().default(""),
  lastName: z.string().default(""),
  email: z.string().email().optional().or(z.literal("")),
  phone: z.string().default("")
};

export const leadInputSchema = z
  .object({
    ...leadContactShape,
    status: z.enum(leadStatusValues).default("new"),
    sourceId: z.string().default(""),
    sourceName: z.string().default(""),
    originDomain: z.string().default(""),
    pageUrl: z.string().default(""),
    referrerUrl: z.string().default(""),
    utm: z.record(z.string(), z.unknown()).default({}),
    serviceInterest: z.string().default(""),
    note: z.string().default(""),
    estimateSnapshot: z.record(z.string(), z.unknown()).default({}),
    emailConsent: z.boolean().default(false),
    smsConsent: z.boolean().default(false),
    lostReason: z.string().default("")
  })
  .refine((value) => Boolean(String(value.email ?? "").trim() || String(value.phone ?? "").trim()), {
    message: "Lead must include an email or phone number.",
    path: ["email"]
  });

export const publicLeadCaptureInputSchema = z
  .object({
    ...leadContactShape,
    captureType: z.enum(["newsletter", "estimator", "website_form", "manual"]).default("website_form"),
    sourceId: z.string().default(""),
    source: z.string().default("website"),
    pageUrl: z.string().default(""),
    referrerUrl: z.string().default(""),
    utm: z.record(z.string(), z.unknown()).default({}),
    serviceInterest: z.string().default(""),
    note: z.string().default(""),
    estimateSnapshot: z.record(z.string(), z.unknown()).default({}),
    emailConsent: z.boolean().default(false),
    smsConsent: z.boolean().default(false)
  })
  .refine((value) => Boolean(String(value.email ?? "").trim() || String(value.phone ?? "").trim()), {
    message: "Lead capture must include an email or phone number.",
    path: ["email"]
  });

export const leadStatusUpdateSchema = z.object({
  status: z.enum(leadStatusValues),
  lostReason: z.string().default(""),
  note: z.string().default("")
});

export const leadNoteInputSchema = z.object({
  note: z.string().min(1)
});

export const leadAutomationStepInputSchema = z.object({
  id: z.string().default(""),
  delayValue: z.number().int().min(1).max(3650),
  delayUnit: z.enum(leadAutomationDelayUnitValues).default("hours"),
  channel: z.enum(leadAutomationChannelValues).default("email"),
  templateId: z.string().default(""),
  smsBody: z.string().default(""),
  generatedOffer: generatedAutomationOfferInputSchema.default({
    enabled: false,
    discountType: "percentage",
    discountValue: 10,
    codePrefix: "",
    expirationDays: 7,
    eligibilityMode: "newsletter_first_booking"
  })
});

export const leadAutomationInputSchema = z.object({
  name: z.string().min(1),
  triggerStatus: z.enum(leadStatusValues).default("new"),
  sourceIds: z.array(z.string()).default([]),
  stopOnBooking: z.boolean().default(true),
  steps: z.array(leadAutomationStepInputSchema).min(1),
  active: z.boolean().default(true)
});

export const leadAutomationJobSchema = z.object({
  accountId: z.string().min(1),
  leadAutomationId: z.string().min(1),
  leadAutomationEnrollmentId: z.string().min(1)
});

export const emailSuppressionSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  reason: z.string(),
  source: z.string(),
  entityType: z.string().default(""),
  entityId: z.string().default(""),
  createdAt: z.string()
});

export const communicationsOverviewSchema = z.object({
  layoutCount: z.number().int(),
  templateCount: z.number().int(),
  campaignCount: z.number().int(),
  scheduledCampaignCount: z.number().int(),
  automationCount: z.number().int(),
  activeAutomationCount: z.number().int(),
  subscriberCount: z.number().int(),
  suppressedCount: z.number().int(),
  sentLast30Days: z.number().int(),
  failedLast30Days: z.number().int(),
  unsubscribedLast30Days: z.number().int(),
  recentTemplates: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      templateType: z.enum(communicationTemplateTypeValues),
      updatedAt: z.string()
    })
  )
});

export const communicationsAnalyticsSummarySchema = z.object({
  sentCount: z.number().int(),
  deliveredCount: z.number().int(),
  openedCount: z.number().int(),
  clickedCount: z.number().int(),
  bouncedCount: z.number().int(),
  complainedCount: z.number().int(),
  failedCount: z.number().int(),
  unsubscribedCount: z.number().int()
});

export const communicationsCampaignAnalyticsRowSchema = z.object({
  campaignId: z.string(),
  campaignName: z.string(),
  sentCount: z.number().int(),
  deliveredCount: z.number().int(),
  openedCount: z.number().int(),
  clickedCount: z.number().int(),
  bouncedCount: z.number().int(),
  complainedCount: z.number().int(),
  failedCount: z.number().int()
});

export const communicationsAutomationAnalyticsRowSchema = z.object({
  automationId: z.string(),
  automationName: z.string(),
  stepNumber: z.number().int(),
  stepDelayDays: z.number().int(),
  sentCount: z.number().int(),
  deliveredCount: z.number().int(),
  openedCount: z.number().int(),
  clickedCount: z.number().int(),
  bouncedCount: z.number().int(),
  complainedCount: z.number().int(),
  failedCount: z.number().int(),
  convertedCount: z.number().int(),
  conversionRate: z.number()
});

export const communicationsAnalyticsSchema = z.object({
  preset: z.string(),
  startAt: z.string(),
  endAt: z.string(),
  summary: communicationsAnalyticsSummarySchema,
  campaigns: z.array(communicationsCampaignAnalyticsRowSchema),
  automations: z.array(communicationsAutomationAnalyticsRowSchema)
});

export const communicationActivityRowSchema = z.object({
  id: z.string(),
  bucket: z.string(),
  messageKind: z.enum(communicationMessageKindValues),
  campaignId: z.string().default(""),
  automationId: z.string().default(""),
  channel: z.string(),
  recipient: z.string(),
  recipientType: z.string(),
  templateKey: z.string(),
  subjectRendered: z.string().default(""),
  status: z.string(),
  deliveryStatus: z.string(),
  errorMessage: z.string().default("")
});

export const singletonSettingsSchemaMap = {
  general: generalSettingsSchema,
  pricebook: pricebookSettingsSchema,
  "booking-form": bookingFormSettingsSchema,
  "frequency-settings": frequencySettingsSchema,
  "google-maps-api": googleMapsApiSettingsSchema,
  "gift-card-storefront": giftCardStorefrontSettingsSchema,
  invoices: invoiceSettingsSchema,
  emails: emailsSettingsSchema,
  "text-messages": textMessagesSettingsSchema,
  waitlist: waitlistSettingsSchema,
  leads: leadsSettingsSchema,
  marketing: marketingSettingsSchema,
  other: otherSettingsSchema
} as const;

export const relationalSettingsSchemaMap = {
  locations: locationInputSchema,
  services: serviceInputSchema,
  "service-scenarios": serviceScenarioInputSchema,
  extras: extraInputSchema,
  "booking-spots": bookingSpotInputSchema,
  "calendar-profiles": calendarProfileInputSchema,
  "frequency-rules": frequencyRuleInputSchema,
  promotions: promotionInputSchema,
  "lead-sources": marketingSourceInputSchema,
  "notification-rules": notificationRuleInputSchema,
  staff: staffInputSchema,
  "staff-time-off": staffTimeOffInputSchema,
  "staff-types": staffTypeInputSchema,
  tags: tagInputSchema,
  "salary-methods": salaryMethodInputSchema
} as const;

export const defaultGeneralSettings = generalSettingsSchema.parse({
  businessName: "Booking Seconds Demo",
  companyWebsite: "",
  customerFacingBaseUrl: "",
  defaultTimezone: "America/Chicago"
});
export const defaultBookingFormSettings = bookingFormSettingsSchema.parse({});
export const defaultPricebookSettings = pricebookSettingsSchema.parse({
  version: 1,
  advancedRules: {
    buildingCommonAreasMinimumChargeCents: 9600,
    residentialFormulaRules: [
      {
        bucket: "apartment_like",
        label: "Apartment / condo",
        baseTeamMinutes: 30,
        minutesPerSquareFoot: 0.0405,
        minutesPerBedroom: 7.5,
        minutesPerFullBathroom: 15,
        minutesPerHalfBathroom: 7.5,
        minutesPerAdditionalLevel: 7.5
      },
      {
        bucket: "townhouse",
        label: "Townhouse",
        baseTeamMinutes: 0,
        minutesPerSquareFoot: 0.035,
        minutesPerBedroom: 7.5,
        minutesPerFullBathroom: 15,
        minutesPerHalfBathroom: 7.5,
        minutesPerAdditionalLevel: 15
      },
      {
        bucket: "single_family",
        label: "Single-family house",
        baseTeamMinutes: 0,
        minutesPerSquareFoot: 0.0375,
        minutesPerBedroom: 15,
        minutesPerFullBathroom: 15,
        minutesPerHalfBathroom: 7.5,
        minutesPerAdditionalLevel: 15
      }
    ],
    residentialComponentFormulaRules: [
      {
        bucket: "apartment_like",
        label: "Apartment / condo",
        setupTeamMinutes: 15,
        kitchenTeamMinutes: 30,
        minutesPerSquareFoot: 0.025,
        minutesPerBedroom: 8,
        minutesPerFullBathroom: 18,
        minutesPerHalfBathroom: 9,
        minutesPerExtraHalfLevel: 5
      },
      {
        bucket: "townhouse",
        label: "Townhouse",
        setupTeamMinutes: 25,
        kitchenTeamMinutes: 30,
        minutesPerSquareFoot: 0.024,
        minutesPerBedroom: 8,
        minutesPerFullBathroom: 18,
        minutesPerHalfBathroom: 9,
        minutesPerExtraHalfLevel: 7.5
      },
      {
        bucket: "single_family",
        label: "Single-family house",
        setupTeamMinutes: 25,
        kitchenTeamMinutes: 30,
        minutesPerSquareFoot: 0.026,
        minutesPerBedroom: 12,
        minutesPerFullBathroom: 18,
        minutesPerHalfBathroom: 9,
        minutesPerExtraHalfLevel: 7.5
      }
    ],
    residentialHourlyServiceProfiles: [
      {
        serviceType: "standard_residential_cleaning",
        label: "Basic Cleaning",
        hourlyRateCentsPerMaidHour: 4800,
        minimumTeamMinutes: 120,
        serviceTimeMultiplier: 1
      },
      {
        serviceType: "deep_cleaning",
        label: "Deep Cleaning",
        hourlyRateCentsPerMaidHour: 5100,
        minimumTeamMinutes: 120,
        serviceTimeMultiplier: 1
      },
      {
        serviceType: "move_in_cleaning",
        label: "Move-In / Move-Out Cleaning",
        hourlyRateCentsPerMaidHour: 5300,
        minimumTeamMinutes: 120,
        serviceTimeMultiplier: 1
      },
      {
        serviceType: "move_out_cleaning",
        label: "Move-In / Move-Out Cleaning",
        hourlyRateCentsPerMaidHour: 5300,
        minimumTeamMinutes: 120,
        serviceTimeMultiplier: 1
      }
    ],
    residentialConditionAdjustmentPercentages: {
      kitchen: { 1: 10, 2: 5, 3: 0, 4: -5, 5: -10 },
      bathrooms: { 1: 10, 2: 5, 3: 0, 4: -5, 5: -10 },
      bedrooms: { 1: 5, 2: 3, 3: 0, 4: -3, 5: -5 },
      otherAreas: { 1: 5, 2: 3, 3: 0, 4: -3, 5: -5 }
    },
    residentialComponentConditionMultipliers: {
      kitchen: { 1: 1.2, 2: 1.1, 3: 1, 4: 0.95, 5: 0.9 },
      bathrooms: { 1: 1.2, 2: 1.1, 3: 1, 4: 0.95, 5: 0.9 },
      bedrooms: { 1: 1.1, 2: 1.05, 3: 1, 4: 0.97, 5: 0.94 },
      otherAreas: { 1: 1.1, 2: 1.05, 3: 1, 4: 0.97, 5: 0.94 }
    },
    residentialFormulaLevelSquareFootageMinimums: [
      { levels: 1, minimumSquareFeet: 1400 },
      { levels: 1.5, minimumSquareFeet: 1600 },
      { levels: 2, minimumSquareFeet: 1800 },
      { levels: 2.5, minimumSquareFeet: 2000 },
      { levels: 3, minimumSquareFeet: 2200 }
    ],
    residentialFormulaMissingHalfLevelIncrement: 200,
    officeCommercialFormula: {
      hourlyRateCentsPerMaidHour: 5100,
      minimumTeamMinutes: 120,
      baseTeamMinutes: 30,
      minutesPerSquareFoot: 0.025,
      minutesPerWorkstation: 3,
      minutesPerPrivateRoom: 8,
      minutesPerRestroom: 15,
      minutesPerBreakroom: 12,
      minutesPerAdditionalLevel: 10,
      manualReviewSquareFeetMax: 6000
    }
  }
});
export const defaultFrequencySettings = frequencySettingsSchema.parse({});
export const defaultGoogleMapsApiSettings = googleMapsApiSettingsSchema.parse({});
export const defaultGiftCardStorefrontSettings = giftCardStorefrontSettingsSchema.parse({});
export const defaultInvoiceSettings = invoiceSettingsSchema.parse({});
export const defaultEmailsSettings = emailsSettingsSchema.parse({
  senderName: "Booking Seconds Demo",
  senderEmail: "hello@bookingseconds.com"
});
export const defaultTextMessagesSettings = textMessagesSettingsSchema.parse({});
export const defaultWaitlistSettings = waitlistSettingsSchema.parse({});
export const defaultLeadsSettings = leadsSettingsSchema.parse({});
export const defaultMarketingSettings = marketingSettingsSchema.parse({});
export const defaultOtherSettings = otherSettingsSchema.parse({});

export type SessionUser = z.infer<typeof sessionUserSchema>;
export type CustomerPortalSession = z.infer<typeof customerPortalSessionSchema>;
export type LoginInput = z.infer<typeof loginInputSchema>;
export type LoginMfaChallengeResponse = z.infer<typeof loginMfaChallengeResponseSchema>;
export type LoginMfaVerifyInput = z.infer<typeof loginMfaVerifyInputSchema>;
export type LoginMfaResendInput = z.infer<typeof loginMfaResendInputSchema>;
export type MfaPhoneInput = z.infer<typeof mfaPhoneInputSchema>;
export type LoginResponse = { user: SessionUser } | LoginMfaChallengeResponse;
export type MobileLoginResponse = { user: SessionUser; sessionToken: string; csrfToken?: string } | LoginMfaChallengeResponse;
export type CustomerMagicLinkRequestInput = z.infer<typeof customerMagicLinkRequestSchema>;
export type CustomerMagicLinkConsumeInput = z.infer<typeof customerMagicLinkConsumeSchema>;
export type CustomerInput = z.infer<typeof customerInputSchema>;
export type CustomerPortalProfileUpdateInput = z.infer<typeof customerPortalProfileUpdateSchema>;
export type AvailabilityQuery = z.infer<typeof availabilityQuerySchema>;
export type QuoteRequest = z.infer<typeof quoteRequestSchema>;
export type BookingCreateInput = z.infer<typeof bookingCreateInputSchema>;
export type PublicBookingCreateInput = z.infer<typeof publicBookingCreateInputSchema>;
export type GiftCardPurchaseSessionInput = z.infer<typeof giftCardPurchaseSessionInputSchema>;
export type GiftCardAdminPurchaseSessionInput = z.infer<typeof giftCardAdminPurchaseSessionInputSchema>;
export type GiftCardAdminFreeIssueInput = z.infer<typeof giftCardAdminFreeIssueInputSchema>;
export type GiftCardLegacyImportInput = z.infer<typeof giftCardLegacyImportSchema>;
export type GiftCardManualIssueInput = z.infer<typeof giftCardManualIssueInputSchema>;
export type GiftCardAdjustmentInput = z.infer<typeof giftCardAdjustmentInputSchema>;
export type GiftCardStatusUpdateInput = z.infer<typeof giftCardStatusUpdateInputSchema>;
export type GiftCardThemeSnapshot = z.infer<typeof giftCardThemeSnapshotSchema>;
export type GiftCardThemeInput = z.infer<typeof giftCardThemeInputSchema>;
export type GiftCardThemeStatusUpdateInput = z.infer<typeof giftCardThemeStatusUpdateInputSchema>;
export type GiftCardTheme = z.infer<typeof giftCardThemeSchema>;
export type GiftCardStorefrontSchedulingPolicy = z.infer<typeof giftCardStorefrontSchedulingPolicySchema>;
export type InvoiceSettings = z.infer<typeof invoiceSettingsSchema>;
export type InvoiceDraftSnapshot = z.infer<typeof invoiceDraftSnapshotSchema>;
export type InvoiceDraftUpdateInput = z.infer<typeof invoiceDraftUpdateSchema>;
export type InvoiceSendInput = z.infer<typeof invoiceSendInputSchema>;
export type GiftCardBuyerRevealPolicy = z.infer<typeof giftCardBuyerRevealPolicySchema>;
export type GiftCardStorefrontSettings = z.infer<typeof giftCardStorefrontSettingsSchema>;
export type PublicWaitlistCreateInput = z.infer<typeof publicWaitlistCreateInputSchema>;
export type BookingUpdateInput = z.infer<typeof bookingUpdateInputSchema>;
export type BookingFeedbackPhoto = z.infer<typeof bookingFeedbackPhotoSchema>;
export type BookingFeedback = z.infer<typeof bookingFeedbackSchema>;
export type BookingFeedbackRatingClickInput = z.infer<typeof bookingFeedbackRatingClickSchema>;
export type BookingFeedbackUpdateInput = z.infer<typeof bookingFeedbackUpdateInputSchema>;
export type StaffFeedbackFeedItem = z.infer<typeof staffFeedbackFeedItemSchema>;
export type StaffLoginAccessUser = z.infer<typeof staffLoginAccessUserSchema>;
export type StaffLoginAccess = z.infer<typeof staffLoginAccessSchema>;
export type StaffLoginCreateInput = z.infer<typeof staffLoginCreateInputSchema>;
export type StaffLoginLinkInput = z.infer<typeof staffLoginLinkInputSchema>;
export type StaffLoginStatusInput = z.infer<typeof staffLoginStatusInputSchema>;
export type StaffLoginPasswordResetInput = z.infer<typeof staffLoginPasswordResetInputSchema>;
export type PriceAdjusterInput = z.infer<typeof priceAdjusterInputSchema>;
export type PriceAdjusterItem = z.infer<typeof priceAdjusterItemSchema>;
export type PriceAdjusterResponse = z.infer<typeof priceAdjusterResponseSchema>;
export type PaymentIntentRequest = z.infer<typeof paymentIntentRequestSchema>;
export type RefundRequest = z.infer<typeof refundRequestSchema>;
export type PaymentChargeQueueQuery = z.infer<typeof paymentChargeQueueQuerySchema>;
export type PaymentChargeBatchInput = z.infer<typeof paymentChargeBatchInputSchema>;
export type PaymentHoldInput = z.infer<typeof paymentHoldInputSchema>;
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;
export type NotificationJob = z.infer<typeof notificationJobSchema>;
export type PaymentWebhookJob = z.infer<typeof paymentWebhookJobSchema>;
export type EmailLayoutInput = z.infer<typeof emailLayoutInputSchema>;
export type EmailTemplateInput = z.infer<typeof emailTemplateInputSchema>;
export type MarketingSubscriberInput = z.infer<typeof marketingSubscriberInputSchema>;
export type MarketingSubscriberImport = z.infer<typeof marketingSubscriberImportSchema>;
export type NewsletterSubscribeInput = z.infer<typeof newsletterSubscribeInputSchema>;
export type EmailTemplatePreviewInput = z.infer<typeof emailTemplatePreviewSchema>;
export type EmailTemplateTestInput = z.infer<typeof emailTemplateTestSchema>;
export type EmailCampaignInput = z.infer<typeof emailCampaignInputSchema>;
export type EmailCampaignScheduleInput = z.infer<typeof emailCampaignScheduleSchema>;
export type CommunicationCampaignJob = z.infer<typeof communicationCampaignJobSchema>;
export type EmailAutomationAudienceFilters = z.infer<typeof emailAutomationAudienceFiltersSchema>;
export type EmailAutomationInput = z.infer<typeof emailAutomationInputSchema>;
export type EmailAutomationStepInput = z.infer<typeof emailAutomationStepInputSchema>;
export type GeneratedAutomationOfferInput = z.infer<typeof generatedAutomationOfferInputSchema>;
export type CommunicationAutomationJob = z.infer<typeof communicationAutomationJobSchema>;
export type MarketingEmailComplianceSettings = z.infer<typeof marketingEmailComplianceSettingsSchema>;
export type LeadsSettings = z.infer<typeof leadsSettingsSchema>;
export type MarketingSourceInput = z.infer<typeof marketingSourceInputSchema>;
export type LeadStatus = z.infer<typeof leadStatusUpdateSchema>["status"];
export type LeadInput = z.infer<typeof leadInputSchema>;
export type PublicLeadCaptureInput = z.infer<typeof publicLeadCaptureInputSchema>;
export type LeadAutomationInput = z.infer<typeof leadAutomationInputSchema>;
export type LeadAutomationStepInput = z.infer<typeof leadAutomationStepInputSchema>;
export type LeadAutomationJob = z.infer<typeof leadAutomationJobSchema>;
export type EmailSuppression = z.infer<typeof emailSuppressionSchema>;
export type CommunicationsOverview = z.infer<typeof communicationsOverviewSchema>;
export type CommunicationsAnalytics = z.infer<typeof communicationsAnalyticsSchema>;
export type CommunicationActivityRow = z.infer<typeof communicationActivityRowSchema>;
export type ExtraInput = z.infer<typeof extraInputSchema>;
export type ServiceScenarioInput = z.infer<typeof serviceScenarioInputSchema>;
export type WaitlistPreferredWindow = z.infer<typeof waitlistPreferredWindowSchema>;
export type WaitlistStatus = z.infer<typeof waitlistStatusSchema>;
export type SchedulerHomeTeamInput = z.infer<typeof schedulerHomeTeamInputSchema>;
export type SchedulerDayQuery = z.infer<typeof schedulerDayQuerySchema>;
export type SchedulerDayBootstrapInput = z.infer<typeof schedulerDayBootstrapInputSchema>;
export type SchedulerDayAutoAssignInput = z.infer<typeof schedulerDayAutoAssignInputSchema>;
export type SchedulerDayStaffOverridesInput = z.infer<typeof schedulerDayStaffOverridesInputSchema>;
export type SchedulerDayTeamInput = z.infer<typeof schedulerDayTeamInputSchema>;
export type SchedulerDayTeamUpdateInput = z.infer<typeof schedulerDayTeamUpdateInputSchema>;
export type SchedulerDayBookingAssignmentUpdateInput = z.infer<typeof schedulerDayBookingAssignmentUpdateSchema>;
export type SchedulerHomeTeam = z.infer<typeof schedulerHomeTeamSchema>;
export type SchedulerDayAvailableStaff = z.infer<typeof schedulerDayAvailableStaffSchema>;
export type SchedulerDayOffStaff = z.infer<typeof schedulerDayOffStaffSchema>;
export type SchedulerDayTeam = z.infer<typeof schedulerDayTeamSchema>;
export type SchedulerBoardBooking = z.infer<typeof schedulerBoardBookingSchema>;
export type SchedulerBookingVisibility = z.infer<typeof schedulerBookingVisibilitySchema>;
export type SchedulerDayBoard = z.infer<typeof schedulerDayBoardSchema>;
export type DriverRouteQuery = z.infer<typeof driverRouteQuerySchema>;
export type DriverRouteAccessInformation = z.infer<typeof driverRouteAccessInformationSchema>;
export type DriverRouteOnWayNotice = z.infer<typeof driverRouteOnWayNoticeSchema>;
export type DriverRouteStaffMember = z.infer<typeof driverRouteStaffMemberSchema>;
export type DriverRouteStop = z.infer<typeof driverRouteStopSchema>;
export type DriverRoute = z.infer<typeof driverRouteSchema>;
export type DriverRouteResponse = z.infer<typeof driverRouteResponseSchema>;
export type DriverOnWayInput = z.infer<typeof driverOnWayInputSchema>;
export type DriverOnWayResult = z.infer<typeof driverOnWayResultSchema>;
export type DriverFieldWorkUpdateInput = z.infer<typeof driverFieldWorkUpdateInputSchema>;
export type DriverFieldWorkUpdateResult = z.infer<typeof driverFieldWorkUpdateResultSchema>;
